import React from 'react';

// MessagingProgram — public page describing the Banyak Group SMS program in full
// detail. This page is referenced from the A2P 10DLC campaign message_flow and
// is what The Campaign Registry (TCR) reviewers navigate to in order to verify
// the program's consent and opt-out workflow. Keep it public (no auth) and keep
// every detail listed below — each line corresponds to a TCR review requirement.

const MessagingProgram = () => {
  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-3xl mx-auto px-6 py-12">
        <div className="mb-8">
          <a href="/" className="text-sm text-blue-600 hover:text-blue-800">&larr; Back to Banyak Group</a>
        </div>

        <div className="bg-white rounded-xl shadow-sm border p-8 space-y-8">
          <header className="border-b pb-6">
            <h1 className="text-2xl font-bold text-gray-900">Banyak Group Recruitment Messaging Program</h1>
            <p className="text-sm text-gray-500 mt-1">SMS / Text Messaging Program Details &middot; Last updated: July 21, 2026</p>
          </header>

          {/* Optionality callout — this is the crux of A2P review: SMS must be
              optional to accessing the service. Stated up front and plainly. */}
          <section className="space-y-2 bg-blue-50/50 border border-blue-200 rounded-lg p-4">
            <h2 className="text-lg font-semibold text-gray-900">SMS Is Entirely Optional</h2>
            <p className="text-sm text-gray-700 leading-relaxed">
              SMS consent is <strong>never required</strong> to use Banyak Group's services. A candidate can join our
              talent network, work with a recruiter, and be considered for any role <strong>without providing a mobile
              number and without agreeing to receive SMS</strong>. SMS is offered only as an optional convenience, and no
              text message is ever sent to anyone who has not explicitly opted in.
            </p>
          </section>

          {/* Program identity */}
          <section className="space-y-3">
            <h2 className="text-lg font-semibold text-gray-900">Program Name</h2>
            <p className="text-sm text-gray-700">Banyak Group Recruitment Messaging</p>
          </section>

          {/* Program description */}
          <section className="space-y-3">
            <h2 className="text-lg font-semibold text-gray-900">Program Description</h2>
            <p className="text-sm text-gray-700 leading-relaxed">
              Banyak Group operates a professional recruitment platform at app.banyak.group used by recruiting firms to manage
              candidate pipelines and conduct individual, person-to-person recruitment communications. SMS messages sent through
              this program are <strong>not</strong> bulk or automated marketing. Each message is initiated by an individual
              recruiter as part of a one-to-one conversation with a candidate the recruiter has already engaged with through a
              prior professional channel.
            </p>
          </section>

          {/* Consent / opt-in */}
          <section className="space-y-3">
            <h2 className="text-lg font-semibold text-gray-900">How Candidates Consent to SMS</h2>
            <p className="text-sm text-gray-700 leading-relaxed">
              Banyak Group uses <strong>explicit, web-form-based SMS consent capture</strong> before any text message is
              sent to a candidate. Consent is always given by ticking an unchecked-by-default checkbox on a web page — we
              never infer consent from a prior relationship, a phone conversation, or the mere fact that we hold a phone
              number. Every SMS recipient has actively opted in through one of the two workflows below, and each opt-in is
              recorded as an audit record on our servers.
            </p>

            <h3 className="text-sm font-semibold text-gray-900 pt-2">Path A — Self-serve opt-in (Talent Network)</h3>
            <p className="text-sm text-gray-700 leading-relaxed">
              Candidates may join our talent network at{' '}
              <code className="bg-gray-100 px-1.5 py-0.5 rounded text-[12px]">https://app.banyak.group/talent-network</code>.
              The intended action of that page is joining the network, which completes with only a name and email address.
              A mobile number field and an unchecked SMS consent checkbox are presented as an <strong>optional</strong>
              add-on: a candidate can submit the form and join without providing a number or ticking the box. An SMS consent
              record is created only when the candidate supplies a mobile number and explicitly ticks the checkbox, which
              contains the verbatim opt-in language, message-frequency disclosure, "message and data rates may apply," and
              STOP / HELP instructions.
            </p>

            <h3 className="text-sm font-semibold text-gray-900 pt-2">Path B — Recruiter-initiated opt-in (per-candidate link)</h3>
            <p className="text-sm text-gray-700 leading-relaxed">
              When a recruiter is already in contact with a candidate through a prior professional channel, the recruiter
              may send that candidate a unique, tokenized consent link so the resulting consent attaches to the specific
              candidate record. The step-by-step workflow is:
            </p>
            <ol className="list-decimal list-inside text-sm text-gray-700 space-y-2 ml-2">
              <li>
                A recruiter, while logged into the Banyak Group platform, generates a unique tokenized consent link tied to a
                specific candidate record. The link has the form
                {' '}<code className="bg-gray-100 px-1.5 py-0.5 rounded text-[12px]">https://app.banyak.group/sms-consent/&lt;token&gt;</code>.
              </li>
              <li>
                The recruiter shares the link with the candidate via an existing professional channel (email, LinkedIn message,
                or in-person follow-up). No SMS is sent at this stage.
              </li>
              <li>
                The candidate opens the link and lands on a public consent page that displays: the candidate's name, the
                recruiter's name and firm, the specific role being discussed, the message frequency disclosure
                ("Message frequency varies. Message and data rates may apply."), opt-out and help instructions
                (<strong>STOP</strong> / <strong>HELP</strong>), and links to this Messaging Program page, the Privacy Policy,
                and the Terms of Service.
              </li>
              <li>
                The candidate enters their mobile phone number into a field on the page and ticks an unchecked-by-default
                consent checkbox that includes the verbatim consent language: <em>"I agree to receive SMS text messages from
                Banyak Group regarding this opportunity and related career discussions. Message frequency varies. Message and
                data rates may apply. I can reply STOP at any time to opt out, or HELP for assistance."</em>
              </li>
              <li>
                On submission, Banyak Group stores an audit record containing the candidate identifier, phone number, IP
                address, user agent string, the exact consent text shown, the recruiter and firm context, and a UTC timestamp.
                This record is retained for the duration of the candidate relationship and is available for compliance review.
              </li>
              <li>
                Only after this record exists, with status <em>granted</em> and no revocation timestamp, can a recruiter send
                an SMS to that candidate. The send is gated server-side by a consent-check middleware that runs before every
                outbound SMS step. Attempts to send without a granted consent record are blocked and logged as skipped.
              </li>
            </ol>

            <h3 className="text-sm font-semibold text-gray-900 pt-2">First message</h3>
            <p className="text-sm text-gray-700 leading-relaxed">
              The first SMS clearly identifies the sending recruiter by name and the Banyak Group platform, references the
              specific role the candidate already consented for, and includes the opt-out reminder
              (<strong>Reply STOP to opt out</strong>).
            </p>

            <h3 className="text-sm font-semibold text-gray-900 pt-2">Out of scope</h3>
            <p className="text-sm text-gray-700 leading-relaxed">
              Banyak Group does <strong>not</strong> use SMS for cold outreach to numbers obtained from purchased lists, for
              promotional broadcasting, or for any contact with a candidate who does not have an active consent record on file.
              Mobile phone numbers and SMS opt-in consent information are not shared with third parties or affiliates for
              marketing or promotional purposes.
            </p>
          </section>

          {/* Message types */}
          <section className="space-y-3">
            <h2 className="text-lg font-semibold text-gray-900">Types of Messages You May Receive</h2>
            <ul className="list-disc list-inside text-sm text-gray-700 space-y-2 ml-2">
              <li>Recruiter outreach regarding a specific role the recruiter believes is a fit, as a continuation of prior contact.</li>
              <li>Interview scheduling confirmations and reminders for interviews you have arranged.</li>
              <li>Application status updates for a role you have applied to.</li>
              <li>Follow-up communications related to an active recruitment conversation.</li>
            </ul>
          </section>

          {/* Sample messages */}
          <section className="space-y-3">
            <h2 className="text-lg font-semibold text-gray-900">Sample Messages</h2>
            <div className="space-y-3">
              <div className="bg-gray-50 border border-gray-200 rounded-lg p-3 text-sm text-gray-800">
                Hi [First Name], this is [Recruiter] from Banyak Group following up on our earlier conversation about the [Title]
                role at [Company]. Would you be open to a quick call this week? Reply STOP to opt out.
              </div>
              <div className="bg-gray-50 border border-gray-200 rounded-lg p-3 text-sm text-gray-800">
                Banyak Group: Your interview with [Company] is confirmed for May 14 at 2:00 PM EST. Reply with any questions.
                Reply STOP to opt out.
              </div>
              <div className="bg-gray-50 border border-gray-200 rounded-lg p-3 text-sm text-gray-800">
                Banyak Group: [Recruiter] has sent you an update regarding the [Title] role. Visit app.banyak.group for details.
                Reply STOP to opt out, HELP for help.
              </div>
            </div>
          </section>

          {/* Frequency */}
          <section className="space-y-3">
            <h2 className="text-lg font-semibold text-gray-900">Message Frequency</h2>
            <p className="text-sm text-gray-700 leading-relaxed">
              <strong>Message frequency varies</strong> based on the active recruitment conversation. Most candidates receive
              between 1 and 4 SMS messages per active conversation. Banyak Group does not send recurring promotional broadcasts.
            </p>
          </section>

          {/* Rates */}
          <section className="space-y-3">
            <h2 className="text-lg font-semibold text-gray-900">Message and Data Rates</h2>
            <p className="text-sm text-gray-700 leading-relaxed">
              <strong>Message and data rates may apply.</strong> Please contact your mobile carrier for details about your
              messaging plan.
            </p>
          </section>

          {/* Opt-out */}
          <section className="space-y-3 bg-blue-50/50 border border-blue-200 rounded-lg p-4">
            <h2 className="text-lg font-semibold text-gray-900">How to Opt Out — Reply STOP</h2>
            <p className="text-sm text-gray-700 leading-relaxed">
              You may opt out of receiving SMS messages at any time by replying{' '}
              <span className="font-extrabold uppercase">STOP</span> to any message you receive from Banyak Group. After
              replying STOP you will receive a single confirmation message and no further SMS messages will be sent. The
              following keywords will also trigger an opt-out: STOP, STOPALL, UNSUBSCRIBE, CANCEL, END, QUIT.
            </p>
          </section>

          {/* Help */}
          <section className="space-y-3 bg-blue-50/50 border border-blue-200 rounded-lg p-4">
            <h2 className="text-lg font-semibold text-gray-900">How to Get Help — Reply HELP</h2>
            <p className="text-sm text-gray-700 leading-relaxed">
              For assistance with SMS messages at any time, reply <span className="font-extrabold uppercase">HELP</span> to any
              message you have received from Banyak Group, or contact us directly at{' '}
              <a href="mailto:oban@banyak.group" className="text-blue-700 underline">oban@banyak.group</a>. You will receive an
              auto-reply with support information.
            </p>
          </section>

          {/* Carriers */}
          <section className="space-y-3">
            <h2 className="text-lg font-semibold text-gray-900">Supported Carriers</h2>
            <p className="text-sm text-gray-700 leading-relaxed">
              SMS services are delivered through Twilio and are available on major US carriers including AT&amp;T, Verizon,
              T-Mobile, and others. Carrier support is not guaranteed and may change.
            </p>
          </section>

          {/* Privacy of mobile data */}
          <section className="space-y-3">
            <h2 className="text-lg font-semibold text-gray-900">Privacy of Mobile Information</h2>
            <p className="text-sm text-gray-700 leading-relaxed">
              Banyak Group does <strong>not</strong> sell, rent, share, or otherwise disclose mobile phone numbers or SMS opt-in
              consent information to any third parties or affiliates for marketing or promotional purposes. Mobile phone numbers
              are used solely to facilitate the recruitment conversations described above.
            </p>
            <p className="text-sm text-gray-700 leading-relaxed">
              For more details see our{' '}
              <a href="/privacy" className="text-blue-700 underline">Privacy Policy</a> and{' '}
              <a href="/terms" className="text-blue-700 underline">Terms of Service</a>.
            </p>
          </section>

          {/* Contact */}
          <section className="space-y-3">
            <h2 className="text-lg font-semibold text-gray-900">Support Contact</h2>
            <p className="text-sm text-gray-700 leading-relaxed">
              Email:{' '}
              <a href="mailto:oban@banyak.group" className="text-blue-700 underline">oban@banyak.group</a>
            </p>
          </section>
        </div>

        <div className="mt-6 text-center text-xs text-gray-500">
          <a href="/privacy" className="text-blue-600 hover:underline">Privacy Policy</a>
          {' · '}
          <a href="/terms" className="text-blue-600 hover:underline">Terms of Service</a>
        </div>
      </div>
    </div>
  );
};

export default MessagingProgram;
